import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { visitApi } from 'src/api/visit';
import { useTranslatedSnackbar } from 'src/hooks/useTranslatedSnackbar';
import { translateKey } from 'src/locales/translate';
import {
  ConflictingVisitDto,
  VisitApiVisitControllerCancelVisitByStaffRequest,
  VisitApiVisitControllerRescheduleVisitByStaffRequest,
} from 'woofwoof-api';
import OrganizationCancelVisitModal from './OrganizationCancelVisitModal';
import OrganizationVisitModal from './OrganizationVisitModal/OrganizationVisitModal';
import { visitDetailsKey } from 'src/sections/@dashboard/visit/utils/visitDetailsQuery';

// ----------------------------------------------------------------------

type ScheduleConflictWarningModalProps = {
  open: boolean;
  /** Cancel — closes the modal WITHOUT applying the schedule change. */
  onClose: () => void;
  /** Confirm — applies the pending schedule change. */
  onConfirm: () => void;
  isConfirming?: boolean;
  conflicts: ConflictingVisitDto[];
};

/** "23/06/2026 19:30" — matches the date+time format used in other tables. */
function formatVisitDateTime(isoString: string): string {
  return format(new Date(isoString), 'dd/MM/yyyy HH:mm');
}

export default function ScheduleConflictWarningModal({
  open,
  onClose,
  onConfirm,
  isConfirming = false,
  conflicts,
}: ScheduleConflictWarningModalProps) {
  const { t } = useTranslation();
  const { enqueueTranslated } = useTranslatedSnackbar();

  const [selectedVisitId, setSelectedVisitId] = useState('');
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isRescheduleLoading, setIsRescheduleLoading] = useState(false);
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  // Visits the user has rescheduled/cancelled from here — hidden from the list.
  const [handledVisitIds, setHandledVisitIds] = useState<string[]>([]);

  // Under the modal's own key, so its invalidations refresh this read; the
  // modal is told the visit is already read and does not read it again.
  const {
    data: selectedVisitData,
    isInitialLoading: isLoadingVisitDetails,
    isError: isVisitDetailsError,
  } = useQuery(
    visitDetailsKey(selectedVisitId),
    () => visitApi.visitControllerGetVisitById({ visitId: selectedVisitId }),
    {
      enabled: !!selectedVisitId,
      retry: 1,
      onError: () => {
        enqueueTranslated('visit.calendarVisitLoadError', { variant: 'error' });
        setSelectedVisitId('');
      },
    },
  );

  const { mutateAsync: rescheduleVisitByStaff } = useMutation({
    mutationFn: (dto: VisitApiVisitControllerRescheduleVisitByStaffRequest) =>
      visitApi.visitControllerRescheduleVisitByStaff(dto),
  });

  const { mutateAsync: cancelVisitByStaff } = useMutation({
    mutationFn: (dto: VisitApiVisitControllerCancelVisitByStaffRequest) =>
      visitApi.visitControllerCancelVisitByStaff(dto),
  });

  const closeVisitModal = () => setSelectedVisitId('');

  const onReschedule = async ({
    professionalId,
    date,
    visitId,
  }: {
    professionalId: string;
    date: string;
    visitId: string;
  }) => {
    try {
      setIsRescheduleLoading(true);
      await rescheduleVisitByStaff({
        visitId,
        rescheduleStaffVisitDto: {
          newStartTime: date,
          newProfessionalId: professionalId,
        },
      });
      setHandledVisitIds((prev) => [...prev, visitId]);
      closeVisitModal();
      enqueueTranslated('snackbar.visitUpdated', { variant: 'success' });
    } catch (error) {
      console.error(error);
    } finally {
      setIsRescheduleLoading(false);
    }
  };

  const onCancelVisit = async (reason?: string) => {
    try {
      setIsCancelLoading(true);
      await cancelVisitByStaff({
        visitId: selectedVisitId,
        cancelStaffVisitDto: {
          cancellationReason:
            reason || String(t('visit.defaultCancelReasonByStaff')),
        },
      });
      setHandledVisitIds((prev) => [...prev, selectedVisitId]);
      setIsCancelModalOpen(false);
      closeVisitModal();
      enqueueTranslated('snackbar.visitCancelled', { variant: 'success' });
    } catch (error) {
      console.error(error);
    } finally {
      setIsCancelLoading(false);
    }
  };

  const visibleConflicts = conflicts.filter(
    (conflict) => !handledVisitIds.includes(conflict.visitId),
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        // Cap height with a viewport gap top & bottom; the table area scrolls so
        // the action button stays visible no matter how many conflicts there are.
        PaperProps={{ sx: { maxHeight: 'calc(100% - 64px)' } }}
      >
        <DialogTitle>
          {translateKey('calendar.schedule.conflictWarningTitle')}
        </DialogTitle>

        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, flexShrink: 0 }}
          >
            {translateKey('calendar.schedule.conflictWarningBody')}
          </Typography>

          <TableContainer sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    {translateKey('calendar.schedule.conflictColDate')}
                  </TableCell>
                  <TableCell>
                    {translateKey('calendar.schedule.conflictColPatient')}
                  </TableCell>
                  <TableCell>
                    {translateKey('calendar.schedule.conflictColProfessional')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleConflicts.map((conflict) => (
                  <TableRow
                    key={conflict.visitId}
                    hover
                    onClick={() => setSelectedVisitId(conflict.visitId)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      {formatVisitDateTime(conflict.startTime)}
                    </TableCell>
                    <TableCell>{conflict.petName || '-'}</TableCell>
                    <TableCell>{conflict.professionalName || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            disabled={isConfirming}
          >
            {translateKey('calendar.schedule.conflictCancel')}
          </Button>
          <LoadingButton
            variant="contained"
            data-testid="timeoff-conflict-confirm"
            onClick={onConfirm}
            loading={isConfirming}
          >
            {translateKey('calendar.schedule.conflictConfirm')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <OrganizationVisitModal
        // Opens at once on its skeleton while the visit is read (#732).
        isOpen={
          Boolean(selectedVisitId) &&
          !isVisitDetailsError &&
          (isLoadingVisitDetails || Boolean(selectedVisitData))
        }
        loading={isLoadingVisitDetails}
        detailsFetched
        onClose={closeVisitModal}
        visit={selectedVisitData?.data}
        onReschedule={onReschedule}
        isRescheduleLoading={isRescheduleLoading}
        onCancel={() => setIsCancelModalOpen(true)}
        isCancelLoading={isCancelLoading}
      />

      <OrganizationCancelVisitModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onAccept={onCancelVisit}
        isLoading={isCancelLoading}
      />
    </>
  );
}
